import { ReactNode, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeatureKey } from '@/types/Licensing.interface';
import { licensingService } from '@/data/licensingService';

interface Props {
  branchId: number;
  featureKey: FeatureKey;
  titleKey: string;
  descriptionKey: string;
  ctaKey: string;
  children: ReactNode;
}

/**
 * Per-branch feature license gate, reused across BranchDetails tabs (POS,
 * Financial, Daily Logins). We fetch the features of the SPECIFIC branch
 * being viewed (not `useFeature()` — that hook tracks the user's currently
 * selected branch, which can differ from the branch this details page is
 * showing).
 *
 * Product decision (originally Faz 12.2, for FISCAL_POS): when the branch's
 * plan lacks the feature we do NOT hide the section. We show it disabled
 * with an upgrade banner + CTA on top, so the customer sees the feature
 * exists and has a clear path to enable it. Hiding entirely was rejected
 * specifically because it removes the sales/upgrade reminder — the same
 * treatment now applies to every gated branch tab.
 *
 * Interaction lockdown uses three complementary layers, so a determined user
 * can't accidentally trigger a mutation on the disabled view:
 *   1. <fieldset disabled> — the HTML-native disable for every form control
 *      inside the tree (inputs, buttons, selects). Works even if MUI's own
 *      disabled logic doesn't cover a custom child.
 *   2. `pointerEvents: 'none'` — kills clicks/hovers on non-form elements
 *      (e.g. Card headers, icon buttons wired via onClick alone).
 *   3. `opacity` + `filter: grayscale(...)` — visual signal that the content
 *      is not live. Independent from the two above so removing one doesn't
 *      silently degrade the disabled affordance.
 * Backend gating is the authoritative enforcement — this UI is a usability
 * layer on top of that, not a security boundary. (Note: as of this writing
 * DAILY_LOGINS is enforced backend-side only on the runtime check-in
 * controller, not on the daily-login-types CRUD this gate wraps — this gate
 * is ahead of that backend boundary intentionally, for UX consistency.)
 */
export default function BranchFeatureGate({
  branchId,
  featureKey,
  titleKey,
  descriptionKey,
  ctaKey,
  children
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [licensed, setLicensed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Falsy / non-positive branchId (e.g. the parseInt(branchId || '0')
    // fallback on the parent page while the URL param resolves): render
    // nothing rather than hitting the API with `branchId=0` and cacheing
    // a "no license" answer against a nonsense key.
    if (!branchId || branchId <= 0) return;
    setLicensed(null);
    licensingService
      .getFeatures(branchId)
      .then((result) => {
        if (cancelled) return;
        const features: string[] = Array.isArray(result?.features)
          ? result.features
          : [];
        setLicensed(features.includes(featureKey));
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed: on a fetch error assume unlicensed so we don't
        // present the manager as usable when we can't confirm it is.
        setLicensed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, featureKey]);

  if (!branchId || branchId <= 0) return null;

  if (licensed === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (licensed) return <>{children}</>;

  return (
    <Box>
      <Alert
        severity="info"
        icon={<LockOutlinedIcon fontSize="inherit" />}
        sx={{ mb: 2 }}
        action={
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={() => navigate('/settings/subscription')}
          >
            {t(ctaKey)}
          </Button>
        }
      >
        <AlertTitle>{t(titleKey)}</AlertTitle>
        <Stack spacing={0.5}>
          <span>{t(descriptionKey)}</span>
        </Stack>
      </Alert>
      <Box
        aria-hidden
        sx={{
          opacity: 0.45,
          filter: 'grayscale(0.6)',
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      >
        <fieldset
          disabled
          style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}
        >
          {children}
        </fieldset>
      </Box>
    </Box>
  );
}
